import React, { useState, useEffect } from "react";
import Admin from "@apis/admin";
import Fields from "@components/fields";
import { Form, Formik, useFormikContext } from "formik";
import { toast } from "react-toastify";
import DropDown from "@components/fields/dropdown";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import Role from "@apis/role";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight,
  ShieldCheck,
  User,
  KeyRound,
  Image as ImageIcon,
} from "lucide-react";

// Helper component to handle manual value updates if needed
const FomikDataFiller = ({ data }: { data: any }) => {
  const { setValues, values } = useFormikContext<any>();

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      setValues({
        ...values,
        name: data.name || "",
        email: data.email || "",
        avatar: data.avatar ? { url: data.avatar } : null,
        roleId: data.roleId || "",
        // Important: Fill from individualPermissions row so role permissions don't become 'sticky' row-level overrides
        permissions: data.individualPermissions || [],
        isActive: data.isActive ?? true,
      });
    }
  }, [data]);

  return null;
};

const CreateSubAdmin = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [initialUserData, setInitialUserData] = useState<any>(null);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    Role.list();
    if (isEdit) {
      fetchAdminDetails();
    }
  }, [id]);

  const fetchAdminDetails = async () => {
    try {
      const response = await Admin.find({ id: Number(id) as any });
      const userData = response?.data || response;

      if (userData && (userData.id || userData.email)) {
        setInitialUserData(userData);
      } else {
        toast.error("User details not found");
      }
    } catch (error) {
      console.error("Failed to fetch admin details:", error);
      toast.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const rolesState = useSelector((state: RootState) => state.roles);
  const roles = Array.isArray(rolesState.data)
    ? rolesState.data
    : rolesState.items || [];

  const roleOptions = roles.map((role: any) => ({
    title: role.name,
    value: role.id,
    permissions: role.permissions || [],
  }));

  const initialValues = {
    name: initialUserData?.name || "",
    email: initialUserData?.email || "",
    password: "",
    confirmPassword: "",
    avatar: initialUserData?.avatar ? { url: initialUserData.avatar } : null,
    roleId: initialUserData?.roleId || "",
    permissions: initialUserData?.individualPermissions || [],
    isActive: initialUserData?.isActive ?? true,
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      if (!values.name || !values.email || (!isEdit && !values.password)) {
        toast.error("Please fill in all required fields.");
        setSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);

      if (values.password) {
        formData.append("password", values.password);
      }

      if (values.roleId) {
        formData.append("roleId", values.roleId);
      }

      if (values.avatar?.rawFile) {
        formData.append("avatar", values.avatar.rawFile);
      }

      // Individual Overrides
      const individualPermissions = values.permissions || [];
      individualPermissions.forEach((permission: string) => {
        formData.append("permissions", permission);
      });

      let promise;
      if (isEdit) {
        promise = Admin.update({
          state: "admins",
          data: formData,
          id: Number(id) as any,
        });
      } else {
        promise = Admin.create({ state: "admins", data: formData });
      }

      await toast.promise(promise, {
        pending: isEdit ? "Updating sub-admin..." : "Creating sub-admin...",
        success: isEdit
          ? "Sub-admin updated successfully!"
          : "Sub-admin created successfully!",
        error: isEdit
          ? "Failed to update sub-admin"
          : "Failed to create sub-admin",
      });

      navigate("/admin/users/sub-admins");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div style={{ margin: "0 auto", paddingBottom: "60px" }}>
      {/* Header Section */}
      <div
        style={{
          marginBottom: "32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            <ChevronRight style={{ transform: "rotate(180deg)" }} size={20} />
          </button>
          <div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                color: "#0f172a",
                margin: "0 0 6px 0",
              }}
            >
              {isEdit ? "Update Team" : "Add Team"}
            </h1>
            <p style={{ margin: 0, color: "#64748b", fontSize: "15px" }}>
              Modify administrator information and individual permissions.
            </p>
          </div>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, setFieldValue, values }) => {
          const selectedRole = roleOptions.find(
            (r) => r.value === values.roleId,
          );
          const rolePermissions = selectedRole?.permissions || [];

          return (
            <Form>
              <FomikDataFiller data={initialUserData} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 350px",
                  gap: "32px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "32px",
                  }}
                >
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      padding: "40px",
                      border: "1px solid #f1f5f9",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "32px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: "#fef2f2",
                          color: "#f23236",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <User size={20} />
                      </div>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 800,
                          color: "#0f172a",
                          margin: 0,
                        }}
                      >
                        Profile Details
                      </h3>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "24px",
                      }}
                    >
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Fields.Input
                          name="name"
                          label="Full Name *"
                          placeholder="E.g. John Doe"
                          required
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Fields.Input
                          name="email"
                          label="Email Address *"
                          type="email"
                          placeholder="admin@zedfo.com"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      padding: "40px",
                      border: "1px solid #f1f5f9",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "32px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: "#fef2f2",
                          color: "#f23236",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ShieldCheck size={20} />
                      </div>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 800,
                          color: "#0f172a",
                          margin: 0,
                        }}
                      >
                        Security & Role
                      </h3>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "24px",
                        marginBottom: "32px",
                      }}
                    >
                      <Fields.Input
                        name="password"
                        label={isEdit ? "New Password" : "Password *"}
                        type="password"
                        placeholder={
                          isEdit
                            ? "Leave blank to keep current"
                            : "Enter password"
                        }
                        required={!isEdit}
                      />
                      <Fields.Input
                        name="confirmPassword"
                        label={
                          isEdit ? "Confirm New Password" : "Confirm Password *"
                        }
                        type="password"
                        placeholder={
                          isEdit
                            ? "Leave blank to keep current"
                            : "Confirm password"
                        }
                        required={!isEdit}
                      />
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #f1f5f9",
                        paddingTop: "32px",
                      }}
                    >
                      <DropDown
                        name="roleId"
                        label="Assigned Role *"
                        options={roleOptions}
                        titleAsKey="title"
                        valueAsKey="value"
                        isMultiple={false}
                        placeholder="Select a functional role"
                        styleInput="width:100%"
                        onChange={(selected: any) => {
                          setFieldValue("roleId", selected);
                          // Clear individual row permissions so they follow the role naturally
                          setFieldValue("permissions", []);
                        }}
                      />
                      {rolePermissions.length > 0 && (
                        <div
                          style={{
                            marginTop: "24px",
                            padding: "16px",
                            background: "#f8fafc",
                            borderRadius: "12px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <p
                            style={{
                              margin: "0 0 12px 0",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#64748b",
                              textTransform: "uppercase",
                            }}
                          >
                            Inherited Role Permissions ({rolePermissions.length}
                            )
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            {rolePermissions.map((p: string, i: number) => (
                              <span
                                key={i}
                                style={{
                                  padding: "4px 8px",
                                  background: "#ffffff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  color: "#475569",
                                  fontWeight: 600,
                                }}
                              >
                                {p.split(".").pop()?.replace(/_/g, " ")}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "32px",
                  }}
                >
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      padding: "32px",
                      border: "1px solid #f1f5f9",
                      textAlign: "center",
                    }}
                  >
                    <Fields.Upload
                      name="avatar"
                      variant="profile"
                      useRawFile={true}
                      size={120}
                      label=""
                    />
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#1e293b",
                        margin: "16px 0 8px 0",
                      }}
                    >
                      Administrator Avatar
                    </h4>
                  </div>
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "24px",
                      padding: "32px",
                      border: "1.5px dashed #e2e8f0",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#475569",
                        margin: "0 0 16px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <KeyRound size={16} /> Account Summary
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#64748b" }}>
                          Role
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#f23236",
                          }}
                        >
                          {selectedRole?.title || "PENDING"}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#64748b" }}>
                          Overrides
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color:
                              values.permissions.length > 0
                                ? "#16a34a"
                                : "#64748b",
                          }}
                        >
                          {values.permissions.length} Manual
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        padding: "16px",
                        borderRadius: "16px",
                        background: "#f23236",
                        color: "#ffffff",
                        border: "none",
                        fontWeight: 700,
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      {isSubmitting
                        ? "Saving..."
                        : isEdit
                          ? "Update Sub Admin"
                          : "Save Sub Admin"}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      style={{
                        padding: "16px",
                        borderRadius: "16px",
                        background: "#ffffff",
                        color: "#475569",
                        border: "1.5px solid #e2e8f0",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateSubAdmin;
